import React from "react";
import { Helmet } from "react-helmet";
import {
  ArrowUpRight,
  Brain,
  Braces,
  Github,
  Hammer,
  Lightbulb,
  MessageCircle,
  MousePointer2,
  Rocket,
  Sparkles,
  TerminalSquare,
  Zap,
} from "lucide-react";

const buildStats = [
  { label: "Shipped builds", value: "20+" },
  { label: "Core stack", value: "Fullstack" },
  { label: "Default mode", value: "Build fast" },
];

const loops = [
  "Everything has already been built",
  "This idea is not good enough",
  "Someone else probably did it better",
  "I'll just scroll for inspiration",
];

const ideonFeatures = [
  {
    icon: Lightbulb,
    title: "Fresh sparks",
    copy: "Startup ideas that feel buildable, not recycled from a listicle with a fake smile.",
  },
  {
    icon: Brain,
    title: "Thinking prompts",
    copy: "A sharper path from blank screen to angle, audience, MVP, and first version.",
  },
  {
    icon: Hammer,
    title: "Execution mode",
    copy: "Enough structure to stop polishing the idea and start making the thing real.",
  },
];

const projects = [
  "School management systems",
  "Voting platforms",
  "SaaS tools",
  "Developer tooling",
  "Ideon",
];

const SinglePagePortfolio = () => {
  return (
    <>
      <Helmet>
        <title>Codewic - Fullstack Developer & Product Builder</title>
        <meta
          name="description"
          content="Fullstack products, SaaS tools, and developer workflows by Codewic."
        />
        <link rel="canonical" href="https://codewic.dev/" />
        <meta property="og:title" content="Codewic - Build the thing" />
        <meta
          property="og:description"
          content="Fullstack products, SaaS tools, and developer workflows by Codewic."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://codewic.dev/" />
        <meta property="og:image" content="https://codewic.dev/assets/brand/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta
          property="og:image:alt"
          content="Codewic portfolio preview with the words Build the thing. Brain online."
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Codewic - Fullstack Developer & Product Builder" />
        <meta
          name="twitter:description"
          content="Fullstack products, SaaS tools, and developer workflows by Codewic."
        />
        <meta name="twitter:image" content="https://codewic.dev/assets/brand/twitter-image.png" />
      </Helmet>

      <main className="min-h-screen w-full max-w-[100vw] overflow-hidden bg-[#11100f] text-stone-100 selection:bg-stone-200 selection:text-stone-950">
        <div className="pointer-events-none fixed inset-0 opacity-[0.045] [background-image:linear-gradient(rgba(255,255,255,.9)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.9)_1px,transparent_1px)] [background-size:42px_42px]" />
        <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_18%_12%,rgba(245,245,244,.16),transparent_32%),radial-gradient(circle_at_88%_8%,rgba(214,211,209,.12),transparent_24%),linear-gradient(180deg,transparent,rgba(0,0,0,.45))]" />

        <nav className="fixed left-1/2 top-4 z-50 w-[calc(100vw_-_1.5rem)] max-w-6xl -translate-x-1/2 rounded-md border border-stone-700/70 bg-[#141312]/85 px-3 py-2 shadow-2xl shadow-black/30 backdrop-blur-xl">
          <div className="flex items-center justify-between gap-3">
            <a href="#top" className="flex items-center gap-2">
              <span className="grid size-9 place-items-center rounded bg-stone-100 text-sm font-black text-stone-950">
                cw
              </span>
              <span className="hidden text-sm font-semibold tracking-wide text-stone-200 sm:block">
                codewic
              </span>
            </a>

            <div className="hidden items-center gap-1 md:flex">
              {["Problem", "Ideon", "Proof", "Contact"].map((item) => (
                <a
                  key={item}
                  href={`#${item.toLowerCase()}`}
                  className="rounded px-3 py-2 text-sm font-medium text-stone-400 transition hover:bg-stone-800 hover:text-stone-100"
                >
                  {item}
                </a>
              ))}
            </div>

            <a
              href="https://ideon.codewic.dev"
              className="inline-flex shrink-0 items-center gap-2 rounded bg-stone-100 px-3 py-2 text-sm font-bold text-stone-950 transition hover:bg-white"
            >
              <span className="hidden min-[420px]:inline">Try Ideon</span>
              <span className="min-[420px]:hidden">Try</span>
              <ArrowUpRight size={16} />
            </a>
          </div>
        </nav>

        <section
          id="top"
          className="relative mx-auto grid min-h-screen w-screen max-w-[100vw] items-center gap-12 px-5 pb-16 pt-28 md:max-w-7xl md:grid-cols-[1.08fr_.92fr] md:px-8 lg:px-10"
        >
          <div className="relative z-10">
            <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-stone-700 bg-stone-950/50 px-3 py-2 text-xs font-bold uppercase tracking-[0.18em] text-stone-300">
              <Sparkles size={15} />
              Out of ideas? Yeah, same.
            </div>

            <h1 className="max-w-4xl text-[clamp(2.15rem,8vw,8.8rem)] font-black leading-[0.92] tracking-normal text-stone-50 sm:text-[clamp(3.3rem,9vw,3.8rem)]">
              <span className="block">I build</span>
              <span className="block">the thing that</span>
              <span className="block">kicks your</span>
              <span className="block">brain back</span>
              <span className="block">online.</span>
            </h1>

            <p className="mt-7 max-w-2xl text-lg leading-8 text-stone-300 md:text-xl">
              I am Codewic, a fullstack developer and product builder. I turn the
              empty VS Code stare into shippable software, useful tools, and the
              kind of momentum that makes you whisper, "wait... this might work."
            </p>

            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <a
                href="https://ideon.codewic.dev"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-stone-100 px-5 py-4 text-sm font-black uppercase tracking-[0.14em] text-stone-950 transition hover:bg-white"
              >
                Open Ideon
                <Rocket size={18} />
              </a>
              <a
                href="#proof"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-stone-700 bg-stone-900/70 px-5 py-4 text-sm font-black uppercase tracking-[0.14em] text-stone-100 transition hover:border-stone-500 hover:bg-stone-800"
              >
                See the work
                <MousePointer2 size={18} />
              </a>
            </div>
          </div>

          <div className="relative z-10 mx-auto w-full max-w-[calc(100vw_-_2.5rem)] md:max-w-xl">
            <div className="rotate-1 rounded-md border border-stone-700 bg-[#171615] p-3 shadow-[0_30px_100px_rgba(0,0,0,.45)]">
              <div className="rounded border border-stone-800 bg-[#0c0c0b]">
                <div className="flex items-center justify-between border-b border-stone-800 px-4 py-3">
                  <div className="flex gap-2">
                    <span className="size-3 rounded-full bg-stone-600" />
                    <span className="size-3 rounded-full bg-stone-500" />
                    <span className="size-3 rounded-full bg-stone-400" />
                  </div>
                  <span className="font-mono text-xs text-stone-500">
                    blank_screen.jsx
                  </span>
                </div>

                <div className="space-y-4 p-5 font-mono text-xs text-stone-300 sm:text-sm">
                  <p className="text-stone-500">{"// open laptop"}</p>
                  <p>
                    <span className="text-stone-500">const</span>{" "}
                    <span className="text-stone-100">idea</span>{" "}
                    <span className="text-stone-500">=</span>{" "}
                    <span className="text-stone-400">null</span>
                  </p>
                  <p>
                    <span className="text-stone-500">while</span> (
                    <span className="text-stone-100">overthinking</span>) {"{"}
                  </p>
                  <p className="pl-5 text-stone-500">scroll("inspiration")</p>
                  <p>{"}"}</p>
                  <p className="overflow-hidden break-words rounded bg-stone-900 px-3 py-3 text-stone-100">
                    ideon.generateSpark() <span className="text-stone-500">=&gt;</span>{" "}
                    "Alright... let's do this."
                  </p>
                </div>
              </div>
            </div>

            <div className="absolute -bottom-8 -left-3 rounded-md border border-stone-700 bg-stone-950 px-4 py-3 shadow-xl shadow-black/40 md:-left-8">
              <div className="flex items-center gap-3">
                <Zap className="text-stone-100" size={20} />
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-stone-500">
                    Current status
                  </p>
                  <p className="font-bold text-stone-100">Momentum restored</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="problem" className="relative border-y border-stone-800 bg-[#151413] py-20">
          <div className="mx-auto grid max-w-7xl gap-10 px-5 md:grid-cols-[.85fr_1.15fr] md:px-8 lg:px-10">
            <div>
              <p className="mb-4 text-sm font-black uppercase tracking-[0.2em] text-stone-500">
                The loop
              </p>
              <h2 className="max-w-xl text-4xl font-black leading-tight text-stone-50 md:text-6xl">
                You are not lazy. You are stuck in idea traffic.
              </h2>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {loops.map((loop, index) => (
                <div
                  key={loop}
                  className="rounded-md border border-stone-800 bg-[#0f0e0d] p-5 transition hover:-translate-y-1 hover:border-stone-600"
                >
                  <span className="font-mono text-xs text-stone-500">
                    0{index + 1}
                  </span>
                  <p className="mt-6 text-xl font-bold leading-snug text-stone-100">
                    "{loop}"
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="ideon" className="relative mx-auto max-w-7xl px-5 py-24 md:px-8 lg:px-10">
          <div className="grid gap-10 lg:grid-cols-[.95fr_1.05fr]">
            <div>
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-stone-700 px-3 py-2 text-xs font-bold uppercase tracking-[0.18em] text-stone-300">
                <Rocket size={15} />
                Introducing Ideon
              </div>
              <h2 className="text-5xl font-black leading-none text-stone-50 md:text-7xl">
                A startup factory for developers who need the first push.
              </h2>
            </div>

            <div className="space-y-5">
              <p className="text-xl leading-8 text-stone-300">
                Ideon does not just hand you another "Top 50 SaaS ideas" pile.
                It generates, shapes, and nudges you into execution mode with a
                direction you can actually build from.
              </p>
              <div className="grid gap-4">
                {ideonFeatures.map(({ icon: FeatureIcon, title, copy }) => (
                  <div
                    key={title}
                    className="grid grid-cols-[auto_1fr] gap-4 rounded-md border border-stone-800 bg-[#171615] p-5"
                  >
                    <div className="grid size-11 place-items-center rounded bg-stone-800 text-stone-100">
                      <FeatureIcon size={21} />
                    </div>
                    <div>
                      <h3 className="text-lg font-black text-stone-50">{title}</h3>
                      <p className="mt-1 leading-7 text-stone-400">{copy}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="proof" className="relative bg-stone-950 py-24">
          <div className="mx-auto max-w-7xl px-5 md:px-8 lg:px-10">
            <div className="mb-10 flex flex-col justify-between gap-6 md:flex-row md:items-end">
              <div>
                <p className="mb-4 text-sm font-black uppercase tracking-[0.2em] text-stone-500">
                  Built by Codewic
                </p>
                <h2 className="max-w-3xl text-4xl font-black leading-tight text-stone-50 md:text-6xl">
                  Fullstack developer. Product builder. Serial "let me build
                  this real quick" guy.
                </h2>
              </div>
              <a
                href="https://github.com/codewic"
                className="inline-flex items-center gap-2 rounded-md border border-stone-700 px-4 py-3 font-bold text-stone-200 transition hover:border-stone-500 hover:bg-stone-900"
              >
                <Github size={18} />
                GitHub
              </a>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              {buildStats.map((stat) => (
                <div key={stat.label} className="rounded-md border border-stone-800 bg-[#151413] p-6">
                  <p className="text-sm uppercase tracking-[0.18em] text-stone-500">
                    {stat.label}
                  </p>
                  <p className="mt-5 text-4xl font-black text-stone-100">{stat.value}</p>
                </div>
              ))}
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-5">
              {projects.map((project) => (
                <div
                  key={project}
                  className="group rounded-md border border-stone-800 bg-[#151413] p-5"
                >
                  <Braces className="mb-10 text-stone-600 transition group-hover:text-stone-200" />
                  <p className="font-bold text-stone-200">{project}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="relative px-5 py-24 md:px-8 lg:px-10">
          <div className="mx-auto max-w-5xl rounded-md border border-stone-700 bg-[#181716] p-6 shadow-2xl shadow-black/30 md:p-10">
            <div className="grid gap-8 md:grid-cols-[1fr_auto] md:items-center">
              <div>
                <TerminalSquare className="mb-8 text-stone-400" size={34} />
                <h2 className="text-4xl font-black leading-tight text-stone-50 md:text-6xl">
                  Ideas are not rare. Clarity is.
                </h2>
                <p className="mt-5 max-w-2xl text-lg leading-8 text-stone-300">
                  Need a product, platform, SaaS tool, or developer workflow
                  brought into focus? Bring the messy thought. I will help turn
                  it into a build.
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <a
                  href="https://ideon.codewic.dev"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-stone-100 px-5 py-4 font-black text-stone-950 transition hover:bg-white"
                >
                  Try Ideon
                  <ArrowUpRight size={18} />
                </a>
                <a
                  href="mailto:hello@codewic.dev"
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-stone-700 px-5 py-4 font-black text-stone-100 transition hover:border-stone-500 hover:bg-stone-900"
                >
                  Let's talk
                  <MessageCircle size={18} />
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default SinglePagePortfolio;
